// src/stock/prets/PretAjouter.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Row, Col, Form, Button, Spinner, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   COMPOSANT RÉUTILISABLE : SELECT AVEC RECHERCHE
   ============================================================ */
const SearchableSelect = ({
    options = [],
    value,
    onChange,
    placeholder = "Sélectionner...",
    searchPlaceholder = "Rechercher...",
    labelKey = "label",
    valueKey = "value",
    emptyMessage = "Aucun résultat",
    icon = "ri-search-line",
    required = false,
    disabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        const s = search.toLowerCase();
        return options.filter(o =>
            String(o[labelKey] || '').toLowerCase().includes(s)
        );
    }, [options, search, labelKey]);

    const selected = options.find(o => String(o[valueKey]) === String(value));

    return (
        <div className="searchable-select" ref={wrapperRef}>
            <div
                className={`searchable-select-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setOpen(o => !o)}
                tabIndex={0}
            >
                <i className={`${icon} me-2`}></i>
                <span className={selected ? '' : 'text-muted'}>
                    {selected ? selected[labelKey] : placeholder}
                </span>
                <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line ms-auto`}></i>
            </div>

            {open && (
                <div className="searchable-select-menu">
                    <div className="searchable-select-search">
                        <i className="ri-search-line"></i>
                        <input
                            type="text"
                            autoFocus
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="searchable-select-options">
                        {filtered.length === 0 ? (
                            <div className="searchable-select-empty">{emptyMessage}</div>
                        ) : (
                            filtered.map((opt, idx) => (
                                <div
                                    key={`${opt[valueKey]}-${idx}`}
                                    className={`searchable-select-option ${String(opt[valueKey]) === String(value) ? 'selected' : ''}`}
                                    onClick={() => {
                                        onChange(opt[valueKey]);
                                        setOpen(false);
                                        setSearch('');
                                    }}
                                >
                                    {opt[labelKey]}
                                    {String(opt[valueKey]) === String(value) && (
                                        <i className="ri-check-line ms-auto" style={{ color: COLORS.primary }}></i>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {required && (
                <input
                    type="text"
                    value={value || ''}
                    required
                    onChange={() => {}}
                    style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
                    tabIndex={-1}
                />
            )}
        </div>
    );
};

/* ============================================================
   PAGE
   ============================================================ */
export default function PretAjouter() {
    const navigate = useNavigate();
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Type d'emprunteur : 'interne' ou 'externe'
    const [typeEmprunteur, setTypeEmprunteur] = useState('interne');

    // État du formulaire : article + emprunteur + dates
    const [pret, setPret] = useState({
        // Champs de l'article (créé à la volée)
        article_reference: '',
        article_designation: '',
        article_valeur_estimee: 0,
        // Emprunteur interne
        id_utilisateur_emprunteur: '',
        // Emprunteur externe
        emprunteur_externe_nom: '',
        emprunteur_externe_prenom: '',
        emprunteur_externe_telephone: '',
        emprunteur_externe_email: '',
        emprunteur_externe_societe: '',
        // Dates
        date_pret: new Date().toISOString().split('T')[0],
        date_retour_prevue: '',
        // Commentaire
        commentaire: ''
    });

    useEffect(() => {
        fetchUtilisateurs();
    }, []);

    const fetchUtilisateurs = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_BASE_URL}/utilisateurs.php?action=list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await res.json();
            setUtilisateurs(result.data || []);
        } catch (err) { console.error(err); }
    };

    const utilisateurOptions = useMemo(() =>
        utilisateurs.map(u => ({
            value: u.id_utilisateur,
            label: `${u.prenom_utilisateur || ''} ${u.nom_utilisateur || ''}`.trim() + (u.email ? ` (${u.email})` : '')
        })), [utilisateurs]
    );

    // ---------- SOUMISSION ----------
  // ---------- SOUMISSION ----------
const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (!pret.article_designation.trim()) {
        setError("La désignation de l'article est obligatoire");
        return;
    }
    if (typeEmprunteur === 'interne' && !pret.id_utilisateur_emprunteur) {
        setError("Veuillez sélectionner un utilisateur emprunteur");
        return;
    }
    if (typeEmprunteur === 'externe' && !pret.emprunteur_externe_nom.trim()) {
        setError("Le nom de l'emprunteur externe est obligatoire");
        return;
    }
    if (!pret.date_retour_prevue) {
        setError("La date de retour prévue est obligatoire");
        return;
    }
    if (new Date(pret.date_retour_prevue) < new Date(pret.date_pret)) {
        setError("La date de retour prévue doit être postérieure à la date du prêt");
        return;
    }

    setSaving(true);
    try {
        const token = localStorage.getItem("token");

        // ✅ UN SEUL APPEL : tout est intégré dans prets
        const pretPayload = {
            action: 'create_pret',
            // Champs article (directement dans prets)
            article_reference: pret.article_reference.trim() || null,
            article_designation: pret.article_designation.trim(),
            article_categorie: null,
            article_valeur_estimee: parseFloat(pret.article_valeur_estimee) || 0,
            // Dates
            date_pret: pret.date_pret,
            date_retour_prevue: pret.date_retour_prevue,
            commentaire: pret.commentaire,
            montant: parseFloat(pret.article_valeur_estimee) || 0,
            type_emprunteur: typeEmprunteur
        };

        if (typeEmprunteur === 'interne') {
            pretPayload.id_utilisateur_emprunteur = pret.id_utilisateur_emprunteur;
        } else {
            pretPayload.emprunteur_externe_nom = pret.emprunteur_externe_nom.trim();
            pretPayload.emprunteur_externe_prenom = pret.emprunteur_externe_prenom.trim() || null;
            pretPayload.emprunteur_externe_telephone = pret.emprunteur_externe_telephone.trim() || null;
            pretPayload.emprunteur_externe_email = pret.emprunteur_externe_email.trim() || null;
            pretPayload.emprunteur_externe_societe = pret.emprunteur_externe_societe.trim() || null;
        }

        const res = await fetch(`${API_BASE_URL}/prets.php`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(pretPayload)
        });
        const result = await res.json();

        if (result.success) {
            setSuccess("Prêt créé avec succès ! Redirection...");
            setTimeout(() => navigate('/stock/prets'), 1500);
        } else {
            setError(result.message || "Erreur lors de la création du prêt");
        }
    } catch (err) {
        setError(err.message);
    } finally {
        setSaving(false);
    }
};

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper"><i className="ri-add-line"></i></div>
                            <div><h5 className="header-title">Nouveau prêt</h5></div>
                        </div>
                        <Button className="btn-modern-outline" onClick={() => navigate('/stock/prets')}>
                            <i className="ri-arrow-left-line"></i> Retour
                        </Button>
                    </div>

                    {error && <div className="alert alert-danger mb-4">{error}</div>}
                    {success && <div className="alert alert-success mb-4">{success}</div>}

                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient">
                                    <h5 className="mb-0 text-white"><i className="ri-hand-coin-line me-2"></i> Informations du prêt</h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">

                                            {/* ===== SECTION ARTICLE (créé à la volée) ===== */}
                                            <Col md={12}>
                                                <div className="section-title">
                                                    <i className="ri-box-3-line me-2"></i> Montant à prêter
                                                </div>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Désignation <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-text"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={pret.article_designation}
                                                            onChange={(e) => setPret({ ...pret, article_designation: e.target.value })}
                                                            placeholder="Ex: Avance sur salaire"
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Référence</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={pret.article_reference}
                                                            onChange={(e) => setPret({ ...pret, article_reference: e.target.value })}
                                                            placeholder="Auto si vide"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Valeur estimée (FCFA)</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-money-dollar-circle-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={pret.article_valeur_estimee}
                                                            onChange={(e) => setPret({ ...pret, article_valeur_estimee: e.target.value })}
                                                            placeholder="0"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* ===== TYPE D'EMPRUNTEUR ===== */}
                                            <Col md={12}>
                                                <div className="section-title mt-2">
                                                    <i className="ri-user-line me-2"></i> Emprunteur
                                                </div>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Type d'emprunteur <span className="text-danger">*</span></Form.Label>
                                                    <div className="type-emprunteur-switch">
                                                        <button
                                                            type="button"
                                                            className={`type-btn ${typeEmprunteur === 'interne' ? 'active' : ''}`}
                                                            onClick={() => setTypeEmprunteur('interne')}
                                                        >
                                                            <i className="ri-user-line"></i>
                                                            <div>
                                                                <strong>Utilisateur interne</strong>
                                                                <small>Employé de l'entreprise</small>
                                                            </div>
                                                            {typeEmprunteur === 'interne' && <i className="ri-checkbox-circle-fill check"></i>}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`type-btn ${typeEmprunteur === 'externe' ? 'active' : ''}`}
                                                            onClick={() => setTypeEmprunteur('externe')}
                                                        >
                                                            <i className="ri-building-line"></i>
                                                            <div>
                                                                <strong>Personne externe</strong>
                                                                <small>Prestataire, visiteur, société</small>
                                                            </div>
                                                            {typeEmprunteur === 'externe' && <i className="ri-checkbox-circle-fill check"></i>}
                                                        </button>
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                            {/* ===== EMPRUNTEUR INTERNE ===== */}
                                            {typeEmprunteur === 'interne' && (
                                                <Col md={12}>
                                                    <Form.Group>
                                                        <Form.Label className="form-label-modern">
                                                            Utilisateur emprunteur <span className="text-danger">*</span>
                                                        </Form.Label>
                                                        <SearchableSelect
                                                            options={utilisateurOptions}
                                                            value={pret.id_utilisateur_emprunteur}
                                                            onChange={(v) => setPret({ ...pret, id_utilisateur_emprunteur: v })}
                                                            placeholder="Sélectionner un utilisateur"
                                                            searchPlaceholder="Rechercher par nom ou email..."
                                                            emptyMessage="Aucun utilisateur trouvé"
                                                            icon="ri-user-line"
                                                            required
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            )}

                                            {/* ===== EMPRUNTEUR EXTERNE ===== */}
                                            {typeEmprunteur === 'externe' && (
                                                <>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">
                                                                Nom <span className="text-danger">*</span>
                                                            </Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={pret.emprunteur_externe_nom}
                                                                    onChange={(e) => setPret({ ...pret, emprunteur_externe_nom: e.target.value })}
                                                                    placeholder="Nom de famille"
                                                                    required
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">Prénom</Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={pret.emprunteur_externe_prenom}
                                                                    onChange={(e) => setPret({ ...pret, emprunteur_externe_prenom: e.target.value })}
                                                                    placeholder="Prénom"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">Téléphone</Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-phone-line"></i></InputGroup.Text>
                                                                <Form.Control
                                                                    type="tel"
                                                                    value={pret.emprunteur_externe_telephone}
                                                                    onChange={(e) => setPret({ ...pret, emprunteur_externe_telephone: e.target.value })}
                                                                    placeholder="+241 xx xx xx xx"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">Email</Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-mail-line"></i></InputGroup.Text>
                                                                <Form.Control
                                                                    type="email"
                                                                    value={pret.emprunteur_externe_email}
                                                                    onChange={(e) => setPret({ ...pret, emprunteur_externe_email: e.target.value })}
                                                                    placeholder="email@exemple.com"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={12}>
                                                        <Form.Group>
                                                            <Form.Label className="form-label-modern">Société / Organisation</Form.Label>
                                                            <InputGroup className="input-modern">
                                                                <InputGroup.Text><i className="ri-building-line"></i></InputGroup.Text>
                                                                <Form.Control
                                                                    type="text"
                                                                    value={pret.emprunteur_externe_societe}
                                                                    onChange={(e) => setPret({ ...pret, emprunteur_externe_societe: e.target.value })}
                                                                    placeholder="Nom de la société ou organisation"
                                                                />
                                                            </InputGroup>
                                                        </Form.Group>
                                                    </Col>
                                                </>
                                            )}

                                            {/* ===== DATES ===== */}
                                            <Col md={12}>
                                                <div className="section-title mt-2">
                                                    <i className="ri-calendar-line me-2"></i> Dates du prêt
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date du prêt <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={pret.date_pret}
                                                            onChange={(e) => setPret({ ...pret, date_pret: e.target.value })}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date de retour prévue <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-check-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={pret.date_retour_prevue}
                                                            onChange={(e) => setPret({ ...pret, date_retour_prevue: e.target.value })}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* ===== COMMENTAIRE ===== */}
                                            <Col md={12} style={{display:'none'}}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Commentaire</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-sticky-note-line"></i></InputGroup.Text>
                                                        <Form.Control as="textarea" rows={3} value={pret.commentaire} onChange={(e) => setPret({ ...pret, commentaire: e.target.value })} placeholder="Motif du prêt..." />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* ===== ACTIONS ===== */}
                                            <Col md={12}>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button type="button" className="btn-modern-outline" onClick={() => navigate('/stock/prets')}>Annuler</Button>
                                                    <Button type="submit" className="btn-modern-primary" disabled={saving}>
                                                        {saving ? <><Spinner size="sm" className="me-2" />Création...</> : <><i className="ri-save-line me-2"></i>Créer le prêt</>}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES ===== */}
            <style>{`
                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid ${COLORS.secondary}; border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary}); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .card-header-gradient { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; padding: 1rem 1.5rem; }
                .form-label-modern { font-weight: 600; color: #334155; font-size: 0.85rem; margin-bottom: 0.4rem; }
                .input-modern .input-group-text { background: #f8fafc; border: 2px solid #e2e8f0; border-right: none; color: ${COLORS.primary}; border-radius: 10px 0 0 10px; }
                .input-modern .form-control, .input-modern .form-select { border: 2px solid #e2e8f0; border-left: none; border-radius: 0 10px 10px 0; padding: 0.6rem 0.9rem; }
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; color: #4b5563; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary { background: linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark || '#1e3f7a'}); border: none; color: white; padding: 0.5rem 1.6rem; border-radius: 50px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .section-title {
                    color: ${COLORS.primary};
                    font-weight: 700;
                    font-size: 1rem;
                    padding-bottom: 0.5rem;
                    border-bottom: 2px solid ${COLORS.secondary};
                    margin-bottom: 0.5rem;
                }

                /* ===== SEARCHABLE SELECT ===== */
                .searchable-select { position: relative; }
                .searchable-select-trigger {
                    display: flex; align-items: center;
                    padding: 0.6rem 0.9rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    background: white;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                    user-select: none;
                }
                .searchable-select-trigger:hover { border-color: #cbd5e1; }
                .searchable-select-trigger.open {
                    border-color: ${COLORS.secondary};
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.12);
                }
                .searchable-select-trigger.disabled {
                    background: #f8fafc;
                    cursor: not-allowed;
                    opacity: 0.7;
                }
                .searchable-select-trigger i:first-child { color: ${COLORS.primary}; }

                .searchable-select-menu {
                    position: absolute;
                    top: calc(100% + 4px);
                    left: 0; right: 0;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.15);
                    z-index: 1000;
                    overflow: hidden;
                    animation: ssFadeIn 0.15s ease-out;
                }
                @keyframes ssFadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .searchable-select-search {
                    display: flex; align-items: center; gap: 8px;
                    padding: 0.7rem 1rem;
                    border-bottom: 1px solid #f1f5f9;
                }
                .searchable-select-search i { color: #94a3b8; }
                .searchable-select-search input {
                    border: none; outline: none;
                    flex: 1; font-size: 0.9rem;
                    background: transparent;
                }
                .searchable-select-options {
                    max-height: 260px;
                    overflow-y: auto;
                    padding: 0.4rem;
                }
                .searchable-select-option {
                    padding: 0.55rem 0.8rem;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 0.88rem;
                    display: flex; align-items: center;
                    transition: background 0.15s ease;
                }
                .searchable-select-option:hover { background: #f1f5f9; }
                .searchable-select-option.selected {
                    background: ${COLORS.primary}10;
                    color: ${COLORS.primary};
                    font-weight: 600;
                }
                .searchable-select-empty {
                    padding: 1rem;
                    text-align: center;
                    color: #94a3b8;
                    font-size: 0.85rem;
                }

                /* ===== TYPE EMPRUNTEUR SWITCH ===== */
                .type-emprunteur-switch {
                    display: flex; gap: 1rem; flex-wrap: wrap;
                }
                .type-btn {
                    flex: 1; min-width: 220px;
                    position: relative;
                    background: white; border: 2px solid #e2e8f0;
                    border-radius: 12px; padding: 1rem 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    text-align: left; cursor: pointer;
                    transition: all .2s ease;
                }
                .type-btn > i:first-child {
                    width: 42px; height: 42px;
                    background: #f1f5f9; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    color: #94a3b8; font-size: 1.3rem;
                    flex-shrink: 0;
                }
                .type-btn > div { display: flex; flex-direction: column; }
                .type-btn > div strong { color: #1e293b; font-size: 0.95rem; }
                .type-btn > div small { color: #64748b; font-size: 0.75rem; margin-top: 0.1rem; }
                .type-btn .check {
                    position: absolute; top: 10px; right: 12px;
                    color: ${COLORS.primary}; font-size: 1.1rem;
                }
                .type-btn:hover { border-color: ${COLORS.secondary}; }
                .type-btn.active {
                    border-color: ${COLORS.primary};
                    background: linear-gradient(135deg, #f0f4fb, #e8f7fc);
                }
                .type-btn.active > i:first-child {
                    background: ${COLORS.primary}; color: white;
                }
            `}</style>
        </>
    );
}
